import { getParams } from '../../utils/searchParams';
import { FullscreenError } from '../../components/FullscreenError/FullscreenError';
import { Layout } from '../../components/Layout/Layout';
import { RequestDetails } from '../../components/RequestDetails/RequestDetails';
import { Loader } from '@repo/ui/Loader';
import { Flex } from '@repo/ui/Flex';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { QUERIES } from '../../utils/constants';
import { getClaimQueryFn } from '../../graphql/query/getClaim';
import type { GetOrderResult, Claim } from '../../graphql/query/getOrder';
import { PageWrap } from '../../components/Page/Page';
import { Promo } from '../../components/Promo/Promo';
import { formatToShort } from '../../utils/dates';
import { hasExplicitWidgetContext } from '../../components/RequestDetails/helpers';
import type { ClaimStatus } from '../../components/RequestDetails/types';
import { useSafeWorkflow } from '../../workflow/hooks';

const getClaimInfo = (
  orderItems: GetOrderResult['order']['items'] | undefined,
  claimId: string,
): { status: ClaimStatus | undefined; claimNumber: string | undefined } => {
  if (!orderItems) return { status: undefined, claimNumber: undefined };

  for (const item of orderItems) {
    const claim = item.claims?.find((c: Claim) => c.claimId === claimId);
    if (claim) {
      return {
        status: claim.status,
        claimNumber: claim.claimNumber ?? undefined,
      };
    }
  }
  return { status: undefined, claimNumber: undefined };
};

export const ClaimDetails = () => {
  const params = getParams();
  const claimId = params?.claimId as string;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { retailer } = useParams({ strict: false });
  const workflow = useSafeWorkflow();

  const {
    isError,
    isLoading,
    data: submittedRequestsData,
  } = useQuery({
    queryKey: [QUERIES.GET_CLAIM, claimId],
    queryFn: () => getClaimQueryFn(claimId),
    retry: false,
  });

  const orderData = queryClient.getQueryData<GetOrderResult>([
    QUERIES.GET_ORDER,
  ]);

  if (isError) {
    return (
      <PageWrap>
        <Flex grow="1" justifyContent="center" alignItems="center">
          <FullscreenError
            title={{
              langKey: ['pdx.claim_lookup.error.title', 'pdx.error.title'],
            }}
            description={{
              langKey: [
                'pdx.claim_lookup.error.description',
                'pdx.error.description',
              ],
            }}
          />
        </Flex>
      </PageWrap>
    );
  }

  if (isLoading || !submittedRequestsData) {
    return (
      <PageWrap>
        <Flex grow="1" justifyContent="center" alignItems="center">
          <Loader size="lg" label="Loading..." />
        </Flex>
      </PageWrap>
    );
  }

  const {
    submitDate,
    deliveryType,
    items,
    packagingOption,
    shipments,
    status,
    location,
    id: requestId,
  } = submittedRequestsData.claim;

  const order = orderData?.order;

  const email =
    workflow?.loginContext?.email ??
    order?.orderTokens?.find((token) => token?.key === 'EMAIL')?.value;

  const replacementTokens = Object.fromEntries(
    Object.entries({
      order_number: order?.orderNumber,
      submitted_date: submitDate
        ? formatToShort(submitDate, params?.locale)
        : undefined,
      delivery_method_type: deliveryType,
      item_count: items?.length ? `${items.length}` : undefined,
      email,
    }).filter(([_, value]) => value !== undefined && value !== null),
  ) as Record<string, string>;

  // Only show email widget if explicitly configured as [[email]] in language keys
  const showEmailWidget = hasExplicitWidgetContext({
    type: deliveryType,
    packaging: packagingOption,
    widgetType: 'email',
  });

  const handleBack = () => {
    navigate({
      to: '/$retailer/item-selection',
      params: { retailer: retailer ?? '' },
      search: params ?? {},
    });
  };

  const { status: claimStatus, claimNumber: orderClaimNumber } = getClaimInfo(
    order?.items,
    claimId,
  );

  const hasItemSelectionStep = workflow?.config?.steps?.some(
    (step) => step.id === 'itemSelection',
  );

  return (
    <PageWrap>
      <Layout>
        <Promo id={'claimDetails.pageTop'} />
        <RequestDetails
          confirmationEmail={showEmailWidget}
          headerTitle={{
            langKey: [
              `pdx.request_details.${claimStatus?.toLowerCase()}.${deliveryType?.toLowerCase()}.${packagingOption?.toLowerCase()}.header.title`,
              `pdx.request_details.${deliveryType?.toLowerCase()}.${packagingOption?.toLowerCase()}.header.title`,
              'pdx.request_details.header.title',
            ],
            replacementTokens,
          }}
          headerSubtitle={{
            langKey: [
              `pdx.request_details.${claimStatus?.toLowerCase()}.${deliveryType?.toLowerCase()}.${packagingOption?.toLowerCase()}.header.subtitle`,
              `pdx.request_details.${deliveryType?.toLowerCase()}.${packagingOption?.toLowerCase()}.header.subtitle`,
              'pdx.request_details.header.subtitle',
            ],
            replacementTokens,
          }}
          data={{
            requestId,
            orderItems: order?.items ?? [],
            requestItems: items ?? [],
            deliveryType,
            deliveryMethod: { packagingOption },
            label: shipments?.[0]?.labelUrl
              ? { url: shipments[0].labelUrl }
              : null,
            qrCode: shipments?.[0]?.qrCodeUrl
              ? { url: shipments[0].qrCodeUrl }
              : null,
            status: status ?? 'REQUEST_COMPLETE',
            location,
            email,
            claimStatus,
            claimId,
            claimNumber: orderClaimNumber,
          }}
          replacementTokens={replacementTokens}
          onBack={hasItemSelectionStep ? handleBack : undefined}
        >
          <Promo id={'claimDetails.pageBottom'} />
        </RequestDetails>
      </Layout>
    </PageWrap>
  );
};
